<template>
  <div :class="['form-group', { 'no-wrapper': noWrapper, 'is-filter-active': modelValue !== '' && modelValue !== null && modelValue !== undefined }]">
    <label v-if="label && !noWrapper" :for="id">
      {{ label }}
      <span v-if="required" class="required-indicator" aria-label="requis">*</span>
      <Tooltip v-if="info" :content="info" placement="top">
        <i
          class="fas fa-info-circle label-info-icon"
          aria-label="Informations"
          tabindex="-1"
        ></i>
      </Tooltip>
      <Tooltip v-if="modelValue !== '' && modelValue !== null && modelValue !== undefined" content="Réinitialiser ce filtre" placement="top">
        <i
          class="fas fa-undo filter-clear-icon"
          aria-label="Réinitialiser ce filtre"
          role="button"
          tabindex="-1"
          @click.stop.prevent="emit('update:modelValue', '')"
        ></i>
      </Tooltip>
    </label>
    <div class="input-wrapper">
      <i
        v-if="icon"
        :class="[icon, 'input-icon input-icon-leading']"
        aria-hidden="true"
      ></i>
      <input
        ref="inputRef"
        :id="id"
        :type="type"
        :inputmode="inputmode"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :autocomplete="autocomplete"
        :list="list"
        :min="min"
        :max="max"
        :step="step"
        :minlength="minlength"
        :maxlength="maxlength"
        :pattern="pattern"
        :data-testid="testId ? testId : undefined"
        :class="['form-control', {
          'form-control-sm': size === 'sm',
          'form-control-cell': size === 'cell',
          'form-control-error': error,
          'form-control-success': showSuccess && !error && modelValue && (validateOnBlur || validateOnInput),
          'has-leading-icon': icon
        }]"
        :aria-label="ariaLabel || (noWrapper && !label ? placeholder : undefined)"
        :aria-describedby="errorId || (hint ? hintId : undefined)"
        :aria-invalid="error ? 'true' : undefined"
        :aria-required="required"
        @input="handleInput"
        @blur="handleBlur"
        @focus="$emit('focus', $event)"
        @keypress="$emit('keypress', $event)"
      />
      <i 
        v-if="error" 
        class="fas fa-exclamation-circle input-icon input-icon-error" 
        aria-hidden="true"
      ></i>
    </div>
    <span v-if="error && !noWrapper" :id="errorId" class="error-text" role="alert" aria-live="polite">{{ error }}</span>
    <span v-if="hint && !error && !noWrapper" :id="hintId" class="hint-text">{{ hint }}</span>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, defineExpose } from 'vue'
import Tooltip from './Tooltip.vue'

const inputRef = ref(null)

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  label: {
    type: String,
    default: null
  },
  type: {
    type: String,
    default: 'text'
  },
  placeholder: {
    type: String,
    default: ''
  },
  inputmode: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: String,
    default: null
  },
  list: {
    type: String,
    default: null
  },
  min: {
    type: [String, Number],
    default: null
  },
  max: {
    type: [String, Number],
    default: null
  },
  step: {
    type: [String, Number],
    default: null
  },
  minlength: {
    type: [String, Number],
    default: null
  },
  maxlength: {
    type: [String, Number],
    default: null
  },
  pattern: {
    type: String,
    default: null
  },
  /**
   * Phrase à mettre dans la bulle du navigateur quand la saisie ne respecte pas
   * `type` ou `pattern`. Sans elle, le navigateur dit « Veuillez respecter le
   * format requis », ce qui n'apprend rien à personne. Le champ vide garde son
   * propre message natif.
   */
  invalidMessage: {
    type: String,
    default: null
  },
  error: {
    type: String,
    default: null
  },
  hint: {
    type: String,
    default: null
  },
  id: {
    type: String,
    default: () => `input-${Math.random().toString(36).substr(2, 9)}`
  },
  /**
   * Taille du champ. `sm` est la taille DENSE : une case de grille éditable,
   * où quinze champs à la taille de formulaire poussent la grille au-delà de
   * sa fenêtre. La cible reste à 36 px. Voir `.form-control-sm` (forms.css).
   *
   * `cell` va un cran plus loin : la case d'un TABLEAU, sans bordure au repos,
   * valeur alignée à droite en chiffres à chasse fixe. Elle se réserve aux
   * champs posés dans une grille qui porte déjà ses propres filets. Voir
   * `.form-control-cell`.
   */
  size: {
    type: String,
    default: null,
    validator: (value) => !value || value === 'sm' || value === 'cell'
  },
  noWrapper: {
    type: Boolean,
    default: false
  },
  ariaLabel: {
    type: String,
    default: null
  },
  validateOnBlur: {
    type: Boolean,
    default: true
  },
  validateOnInput: {
    type: Boolean,
    default: false
  },
  showSuccess: {
    type: Boolean,
    default: true
  },
  testId: {
    type: String,
    default: null
  },
  info: {
    type: String,
    default: null
  },
  trim: {
    type: Boolean,
    default: false
  },
  // Icône Font Awesome affichée à gauche de l'input (ex: 'fas fa-search')
  icon: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'keypress', 'validate'])

const errorId = computed(() => props.error ? `${props.id}-error` : null)
const hintId = computed(() => props.hint ? `${props.id}-hint` : null)

/**
 * Le formulaire n'est pas en `novalidate` : le navigateur bloque l'envoi avant
 * tout code Vue, et c'est SA bulle que le visiteur lit. On y met donc notre
 * phrase, plutôt que d'attendre un bandeau qui ne s'affichera jamais.
 */
const appliquerMessageDeFormat = () => {
  const champ = inputRef.value
  if (!champ || !props.invalidMessage) return
  champ.setCustomValidity('')
  if (champ.validity.patternMismatch || champ.validity.typeMismatch) {
    champ.setCustomValidity(props.invalidMessage)
  }
}

watch(() => props.modelValue, () => nextTick(appliquerMessageDeFormat))
onMounted(appliquerMessageDeFormat)

const handleInput = (e) => {
  const value = props.trim ? e.target.value.trim() : e.target.value
  emit('update:modelValue', value)
  appliquerMessageDeFormat()
  if (props.validateOnInput && value) {
    emit('validate', value)
  }
}

const handleBlur = (e) => {
  emit('blur', e)
  if (props.validateOnBlur) {
    emit('validate', e.target.value)
  }
}

defineExpose({
  inputRef
})
</script>

<style scoped>
/* Les styles .form-group, .form-control, .error-text sont maintenant dans styles/forms.css */
/* On garde uniquement les styles spécifiques au composant */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-control-error {
  border-color: var(--color-danger);
  padding-right: 2.5rem;
}

.form-control-success {
  border-color: var(--color-success);
  /* Le retrait laisse la place à l'icône de validation, posée en absolu à
     droite. Sans lui, elle chevauche la valeur de TOUT champ validé de
     l'application — le besoin ne concernait que les cases étroites de la
     grille des formules, qui le neutralisent chez elles. */
  padding-right: 2.5rem;
}

/* Les cases sans enveloppe sont trop étroites pour porter l'icône ET la
   valeur : là, c'est la valeur qui gagne. */
.no-wrapper .form-control-success {
  padding-right: 0.9rem;
}

.input-icon {
  position: absolute;
  right: 0.75rem;
  pointer-events: none;
  font-size: 1.1rem;
}

.input-icon-success {
  color: var(--color-success);
}

.input-icon-error {
  color: var(--color-danger);
}

/* Icône leading (ex: loupe de recherche) : positionnée à gauche */
.input-icon-leading {
  left: 0.75rem;
  right: auto;
  font-size: 1rem;
  color: var(--color-text-muted);
}

.form-control.has-leading-icon {
  padding-left: 2.25rem;
}

.no-wrapper {
  margin-bottom: 0;
}

.no-wrapper label {
  display: none;
}

.filter-clear-icon {
  display: none;
  margin-left: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--color-primary);
  transition: color var(--transition-base);
  vertical-align: middle;
}

.label-info-icon {
  margin-left: 6px;
  cursor: help;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  transition: color var(--transition-base);
  vertical-align: middle;
}

.label-info-icon:hover {
  color: var(--color-primary);
}

label :deep(.tooltip-wrapper) {
  display: inline-flex;
  width: auto;
}

.filter-clear-icon:hover {
  color: var(--color-danger);
}
</style>

